import type { TedIntonationSpan } from "@/types/screenplay";
import type { ScreenplayEditorBlock } from "./screenplayEditor";
import { createGeminiSpan, removeDeliveryRange } from "./geminiDelivery";
import { createTedIntonationSpec } from "./tedIntonation";
import { inlineMarkers, isInlineMarker } from './inlineMarkers';
import type { EditorGeminiDirection } from './pauseDirections';

export const PAUSE_TEXT = "{Pause}";
export const isPauseMarker = (span?: TedIntonationSpan) => span?.gemini?.pauseMarker === true;

export function pauseWordBoundary(text: string, offset: number) {
  const word = (char: string) => /[\p{L}\p{N}\p{M}'’\-]/u.test(char);
  if (offset <= 0 || offset >= text.length || !word(text[offset - 1]) || !word(text[offset])) return offset;
  let start = offset, end = offset;
  while (start > 0 && word(text[start - 1])) start--;
  while (end < text.length && word(text[end])) end++;
  return offset - start < end - offset ? start : end;
}

/** Keep pause tokens atomic and remap untouched annotations as text moves. */
export function replaceEditorText(block: ScreenplayEditorBlock, start: number, end: number, inserted: string) {
  let from = Math.max(0, Math.min(start, block.text.length));
  let to = Math.max(from, Math.min(end, block.text.length));
  for (const span of block.intonation?.spans ?? []) {
    if (isInlineMarker(span) && (span.start < to && span.end > from || from === to && span.start < from && from < span.end)) {
      from = Math.min(from, span.start); to = Math.max(to, span.end);
    }
  }
  const text = (block.text.slice(0, from) + inserted + block.text.slice(to)).replaceAll('[Pause]',PAUSE_TEXT);
  const delta = inserted.length - (to - from);
  let spans = (block.intonation?.spans ?? []).flatMap(span => {
    if (span.end <= from) return [span];
    if (span.start >= to) return [{ ...span, start: span.start + delta, end: span.end + delta }];
    // Editing within a delivery passage invalidates that passage, not its neighbours.
    return [];
  });
  // Plain-text copy/paste of our visible token remains silent, even without its
  // original metadata. A newly pasted marker starts at the default one second.
  for (const {start,end,cue} of inlineMarkers(text)) {
    if (spans.some(s => isInlineMarker(s) && s.start === start && s.end === end)) continue;
    spans = [...removeDeliveryRange(spans, start, end, text), createGeminiSpan(start, end, text,
      cue === 'pause' ? { pauseMarker: true, pauseMs: 1000, unspoken: true } : { cueMarker: true, cue, unspoken: true })];
  }
  spans=spans.map(span=>({...span,selectedText:span.selectedText.replaceAll('[Pause]',PAUSE_TEXT)}));
  spans.sort((a, b) => a.start - b.start);
  return { block: { ...block, text, intonation: spans.length ? { ...(block.intonation ?? createTedIntonationSpec()), spans } : undefined }, caret: from + inserted.length };
}

export function editEditorText(block: ScreenplayEditorBlock, next: string) {
  let start = 0;
  while (start < block.text.length && start < next.length && block.text[start] === next[start]) start++;
  let end = block.text.length, nextEnd = next.length;
  while (end > start && nextEnd > start && block.text[end - 1] === next[nextEnd - 1]) { end--; nextEnd--; }
  return replaceEditorText(block, start, end, next.slice(start, nextEnd));
}

export function insertInlinePause(block: ScreenplayEditorBlock, offset: number) {
  return insertInlineMarker(block,offset,PAUSE_TEXT,{ pauseMarker: true, pauseMs: 1000, unspoken: true });
}

export function insertInlineMarker(block: ScreenplayEditorBlock, offset: number, token: string, direction: EditorGeminiDirection) {
  let at = Math.max(0, Math.min(offset, block.text.length));
  const containing = block.intonation?.spans.find(s => isInlineMarker(s) && s.start < at && at < s.end);
  if (containing) at = containing.end;
  // Keep literal words on either side unchanged, including insertion inside a word.
  const text = block.text.slice(0, at) + token + block.text.slice(at);
  const spans: TedIntonationSpan[] = (block.intonation?.spans ?? []).flatMap(span => {
    if (span.end <= at) return [span];
    if (span.start >= at) return [{ ...span, start: span.start + token.length, end: span.end + token.length }];
    const { cue: _cue, pauseMs: _pause, ...continuing } = span.gemini ?? {};
    return [
      { ...span, end: at, selectedText: block.text.slice(span.start, at) },
      { ...span, id: `${span.id}:after-marker`, start: at + token.length, end: span.end + token.length,
        selectedText: block.text.slice(at, span.end), ...(span.gemini ? { gemini: continuing } : {}) },
    ];
  });
  const marker = createGeminiSpan(at, at + token.length, text, direction);
  spans.push(marker); spans.sort((a, b) => a.start - b.start);
  return { block: { ...block, text, intonation: { ...(block.intonation ?? createTedIntonationSpec()), spans } }, marker };
}

/** Same-length notation update: keep every saved offset, duration and style. */
export function normalizeInlinePauseNotation(block: ScreenplayEditorBlock): ScreenplayEditorBlock {
  if (!block.text.includes('[Pause]')) return block;
  const text=block.text.replaceAll('[Pause]',PAUSE_TEXT);
  return {...block,text,intonation:block.intonation && {...block.intonation,spans:block.intonation.spans.map(s=>({...s,selectedText:s.selectedText.replaceAll('[Pause]',PAUSE_TEXT)}))}};
}

export function setInlinePauseDuration(block: ScreenplayEditorBlock, id: string, seconds: number) {
  if (!Number.isInteger(seconds) || seconds < 1 || seconds > 7) throw new Error("Choose a pause from 1 to 7 seconds.");
  return { ...block, intonation: block.intonation && { ...block.intonation, spans: block.intonation.spans.map(s =>
    s.id === id && isPauseMarker(s) ? { ...s, gemini: { pauseMarker: true, pauseMs: seconds * 1000, unspoken: true } } : s) } };
}
